<script setup lang="ts">
import type { MDBVegcoBucket } from '@/types/vegco/bucket'
import { emptyMongoId, roundTo, toDate_wd_dd_MMMM } from '@/utils/helpers'
import { unitésParBoîte } from '@/utils/helpers-vegco'

const emits = defineEmits<{
  (e: 'openBucket', bucket: MDBVegcoBucket): void
}>()

const { bucket, currentDate, selectedCodeProduit } = defineProps<{
    bucket?: MDBVegcoBucket,
    currentDate?: Date,
    selectedCodeProduit?: string
}>()

const storeU = useUtilisateurStore()
const storeVegco = useVegcoStore()
const isPublished = computed(() => new Date(bucket?.datePublication ?? '').getFullYear() > 2000)
const isCommande = computed(() => bucket?.commandeVenteId != emptyMongoId())
const isInventory = computed(() => bucket?.semiId == emptyMongoId() && bucket?.commandeVenteId == emptyMongoId())
const comesFromInventory = computed(() => bucket?.semiId == emptyMongoId())
const unitParBoîte = computed(() => unitésParBoîte(bucket?.culture))
const isPartialConfirmed = computed(() =>
  bucket == undefined ? false :
  (isCommande.value && bucket.quantitéConfirmée > 0 && bucket.quantitéConfirmée < bucket.quantité))
const isConfirmed = computed(() =>
  bucket == undefined ? false :
  (isCommande.value && bucket.quantitéConfirmée > 0 && bucket.quantitéConfirmée == bucket.quantité))
const isPrévision = computed(() => bucket == undefined ? false : bucket.estPrévision)

function calculatePriority(bucket: MDBVegcoBucket | undefined): number {
  if (bucket == undefined) {
    return 0
  }

  let priorité = bucket.priorité

  if (currentDate != undefined) {
    const date1 = new Date(bucket.date)
    const date2 = new Date(currentDate)
    date1.setHours(0, 0, 0, 0)
    date2.setHours(0, 0, 0, 0)
    const diff = Math.abs(
      Math.floor((date2.getTime() - date1.getTime()) / (1000 * 60 * 60 * 24))
    )
    priorité += diff * 15
  }

  const balises = storeU.getBalises('Bucket', 'Culture', bucket.culture,
    'Variété', bucket.variété)

  for (const balise of bucket.balises) {
    if (balise == 'Urgent' || bucket.localisation == 'Inventaire') {
      const baliseData = balises.find(b => b.nom == balise)
      if (baliseData != undefined) {
        priorité += baliseData.changementPriorité
      }
    }
  }

  if (priorité >= 100) return 3
  if (priorité >= 75) return 2
  if (priorité > 50) return 1
  return 0
}

const producteurColor = computed(() => {
  return storeVegco.getSiteCouleur(storeU.getSiteLettresDappel(bucket?.siteId)) ?? 'grey'
})

const color = computed(() => {
  switch (calculatePriority(bucket)) {
    case 1:
      return 'yellow-darken-1'
    case 2:
      return 'orange'
    case 3:
      return 'red'
  }
  return 'green'
})

const icon = computed(() => {
  if (isInventory.value) {
    return 'M6 19H8V21H6V19M12 3L2 8V21H4V13H20V21H22V8L12 3M8 11H4V9H8V11M14 11H10V9H14V11M20 11H16V9H20V11M6 15H8V17H6V15M10 15H12V17H10V15M10 19H12V21H10V19M14 19H16V21H14V19Z'
  }

  switch (calculatePriority(bucket)) {
    case 1:
      return 'M4,11V13H16L10.5,18.5L11.92,19.92L19.84,12L11.92,4.08L10.5,5.5L16,11H4Z'
    case 2:
      return 'M13,20H11V8L5.5,13.5L4.08,12.08L12,4.16L19.92,12.08L18.5,13.5L13,8V20Z'
    case 3:
      return 'M19,19H5V5H19M19,3H5A2,2 0 0,0 3,5V19A2,2 0 0,0 5,21H19A2,2 0 0,0 21,19V5C21,3.89 20.1,3 19,3M11,15H13V17H11V15M11,7H13V13H11V7'
  }
  return 'M11,4H13V16L18.5,10.5L19.92,11.92L12,19.84L4.08,11.92L5.5,10.5L11,16V4Z'
})

const title = computed(() => {
  return storeVegco.getSiteLettresDappelRenommé(bucket?.siteId) ?? '?'
})

const valuePrévisionRaw = computed(() => {
  if (bucket == undefined) {
    return undefined
  }

  if (currentDate != undefined && isInventory.value) {
    return storeVegco.getQuantitéPrévision(bucket.codeProduit, bucket.quantitéParPalette, currentDate)
  }

  return undefined
})

const valuePrévision = computed(() => {
  if (!produit.value || valueSmall.value == undefined)
     return valuePrévisionRaw.value ?? 0
  return roundTo((valuePrévisionRaw.value ?? 0) / produit.value.quantité, 0)
})

const valueSmall = computed(() => {
  if (bucket == undefined) {
    return undefined
  }

  // Remove small number from inventory commande buckets
  if (bucket.semiId == emptyMongoId() && bucket.commandeVenteId != emptyMongoId()) {
    return undefined
  }

  // Remove 0 from semi buckets
  if (bucket.semiId != emptyMongoId() && bucket.commandeVenteId == emptyMongoId() && bucket.quantitéConfirmée == 0) {
    return undefined
  }

  return roundTo(bucket.quantitéConfirmée / unitParBoîte.value, 0)
})

const value = computed(() => {
  if (bucket == undefined) {
    return 0
  }

  let qté = bucket.quantité

  const prév = valuePrévisionRaw.value
  if (prév != undefined) {
    qté += prév
  }

  return roundTo(qté / unitParBoîte.value, 0)
})

const produit = computed(() => storeU.getVegcoProduit(bucket?.codeProduit))

const valueUnitSmall = computed(() => {
  if (!produit.value || valueSmall.value == undefined)
     return valueSmall.value
  return roundTo(valueSmall.value * unitParBoîte.value / produit.value.quantité, 0)
})

const valueUnit = computed(() => {
  if (!produit.value)
    return value.value
  return roundTo(value.value * unitParBoîte.value / produit.value.quantité, 0)
})

const palette = computed(() => {
  if (!produit.value || bucket == undefined)
    return undefined
  return roundTo(bucket.quantitéParPalette / produit.value.quantité, 0)
})

const unit = computed(() => {
  if (!bucket) {
    return '?'
  }

  if (bucket?.codeProduit != '')
    return bucket?.codeProduit

  if (bucket?.balises.length == 0)
      return '?'

  if (bucket?.balises.includes('Wrap') && bucket?.balises.includes('Combo') && bucket?.balises.includes('Liner')) {
    const buckets = bucket.balises.filter(b => b != 'Wrap' && b != 'Combo' && b != 'Liner')
    buckets.push('Tous')
    return buckets.join('/')
  }

  if (bucket?.balises.includes('Frais') && bucket?.balises.includes('Coeur') && bucket?.balises.includes('Transfo')) {
    const buckets = bucket.balises.filter(b => b != 'Frais' && b != 'Coeur' && b != 'Transfo')
    buckets.push('Tous')
    return buckets.join('/')
  }

  return bucket.balises.join('/')
})

const semi = computed(() => {
  return bucket?.numéroSemi ?? ''
})

const estValidePourLeCodeDeProduit = computed(() => {
  if (bucket == undefined || selectedCodeProduit == '' || selectedCodeProduit == undefined) {
    return false
  }

  if (bucket.codeProduit == selectedCodeProduit)
    return true

  if (bucket.codeProduit == '') {
    const produit = storeU.getVegcoProduit(selectedCodeProduit)
    if (produit == undefined) {
      return false
    }

    if (bucket.balises.includes(produit.balise))
      return true
  }

  return false
})

function openBucket() {
  if (bucket == undefined) {
    return
  }
  emits('openBucket', bucket)
}
</script>

<template>
  <v-card
    class="stats-card v-alert--border-top"
    :style="{ borderColor: estValidePourLeCodeDeProduit ? 'green' : 'rgba(0, 0, 0, 0.12)', borderWidth: '2px', borderStyle: 'solid' }">
    <span>
      <v-icon
        size="x-large" class="stats-icon"
        :color="color" :icon="icon"/>
    </span>
    <span class="position-absolute" style="top: 10px; left: 35px;">
      <v-btn
        density="compact" variant="tonal" color="primary"
        icon="M13,9H11V7H13M13,17H11V11H13M12,2A10,10 0 0,0 2,12A10,10 0 0,0 12,22A10,10 0 0,0 22,12A10,10 0 0,0 12,2Z" style="opacity: 0.5" @click="openBucket"/>
    </span>
    <span class="position-absolute" style="top: 10px; left: 65px;">
      <v-icon
        v-if="(bucket?.commentaires.length ?? 0) > 2" size="x-large" color="gray"
        icon="M12,3C17.5,3 22,6.58 22,11C22,15.42 17.5,19 12,19C10.76,19 9.57,18.82 8.47,18.5C5.55,21 2,21 2,21C4.33,18.67 4.7,17.1 4.75,16.5C3.05,15.07 2,13.13 2,11C2,6.58 6.5,3 12,3Z" style="opacity: 0.5"/>
      <v-tooltip activator="parent" location="bottom">{{ bucket?.commentaires }}</v-tooltip>
    </span>
    <span class="position-absolute" style="top: 10px; left: 95px;">
      <v-icon
        v-if="bucket?.semiFermé" size="x-large" color="red"
        icon="M19,6.41L17.59,5L12,10.59L6.41,5L5,6.41L10.59,12L5,17.59L6.41,19L12,13.41L17.59,19L19,17.59L13.41,12L19,6.41Z" style="opacity: 0.5"/>
      <v-tooltip activator="parent" location="bottom">Semi fermé</v-tooltip>
    </span>

    <span class="position-absolute" style="top: 40px; left: 5px;">
      <v-icon
        v-if="isPublished" size="x-large" color="primary"
        icon="M12,19.2C9.5,19.2 7.29,17.92 6,16C6.03,14 10,12.9 12,12.9C14,12.9 17.97,14 18,16C16.71,17.92 14.5,19.2 12,19.2M12,5A3,3 0 0,1 15,8A3,3 0 0,1 12,11A3,3 0 0,1 9,8A3,3 0 0,1 12,5M12,2A10,10 0 0,0 2,12A10,10 0 0,0 12,22A10,10 0 0,0 22,12C22,6.47 17.5,2 12,2Z" style="opacity: 0.5"/>
      <v-tooltip activator="parent" location="bottom">Publié</v-tooltip>
    </span>
    <span class="position-absolute" style="top: 40px; left: 35px;">
      <v-icon
        v-if="isPartialConfirmed" size="x-large" color="yellow-darken-1"
        icon="M12 2C6.5 2 2 6.5 2 12S6.5 22 12 22 22 17.5 22 12 17.5 2 12 2M10 17L5 12L6.41 10.59L10 14.17L17.59 6.58L19 8L10 17Z" style="opacity: 0.5"/>
      <v-tooltip activator="parent" location="bottom">Est partiellement confirmé</v-tooltip>
    </span>
    <span class="position-absolute" style="top: 40px; left: 35px;">
      <v-icon
        v-if="isConfirmed" size="x-large" color="green"
        icon="M12 2C6.5 2 2 6.5 2 12S6.5 22 12 22 22 17.5 22 12 17.5 2 12 2M10 17L5 12L6.41 10.59L10 14.17L17.59 6.58L19 8L10 17Z" style="opacity: 0.5"/>
      <v-tooltip activator="parent" location="bottom">Est complètement confirmé</v-tooltip>
    </span>
    <span class="position-absolute" style="top: 40px; left: 65px;">
      <v-icon
        v-if="isPrévision" size="x-large" color="white"
        icon="M12,2A10,10 0 0,0 2,12A10,10 0 0,0 12,22A10,10 0 0,0 22,12A10,10 0 0,0 12,2M16.2,16.2L11,13V7H12.5V12.2L17,14.9L16.2,16.2Z" style="opacity: 0.5"/>
      <v-tooltip activator="parent" location="bottom">Est prévisionnel</v-tooltip>
    </span>

    <div class="card-title ml-auto text-right">
      <span
        class="card-title--name font-weight-bold mb-2"
        :style="{ color: producteurColor }"
        v-text="title"/>
      <h3 v-if="unit">
        {{ unit }}
        <span v-if="palette"> [{{ palette }}]</span>
      </h3>
      <h3
        v-if="valueUnitSmall != undefined"
        class="font-weight-regular d-inline-block ml-2 text-grey"
        style="font-size: 12px">
        {{ valueUnitSmall }} /
        <v-tooltip v-if="isInventory" activator="parent" location="bottom">Inventaire réel</v-tooltip>
        <v-tooltip v-else-if="!isCommande" activator="parent" location="bottom">Montant déjà reçu</v-tooltip>
        <v-tooltip v-else activator="parent" location="bottom">Montant enregistré réellement</v-tooltip>
      </h3>
      <h3
        class="font-weight-regular d-inline-block ml-2"
        style="font-size: 18px">
        {{ valueUnit }}
        <small v-if="!produit"> bte{{ unitParBoîte }}</small>
        <v-tooltip v-if="valuePrévisionRaw != undefined" activator="parent" location="bottom">
          Avec prévision de: {{valuePrévision}}</v-tooltip>
      </h3>

      <v-divider />
    </div>
    <div class="v-alert__border" :class="`text-${color}`"/>
    <div
      class="text-grey text-right stats-footer text-caption">
      <span v-if="isInventory" class="semi">{{ bucket?.quantitéConfirmée && bucket.date ? toDate_wd_dd_MMMM(bucket.date) : '' }}</span>
      <span v-else-if="comesFromInventory" class="semi">{{ toDate_wd_dd_MMMM(bucket?.date ?? '') }}</span>
      <span v-else class="semi">{{ semi }} | {{ toDate_wd_dd_MMMM(bucket?.date ?? '') }}</span>
    </div>
  </v-card>
</template>

<style scoped>
.stats-card {
  padding: 5px;
  padding-top: 10px;
  .semi {
    font-style: italic;
    font-weight: bold;
  }
  .card-title {
    width: fit-content;
    .card-title--name {
      display: inline-block;
      backdrop-filter: blur(3px);
    }
  }
  .caption {
    font-size: 12px;
    letter-spacing: 0;
  }
  .stats-icon {
    position: absolute;
    opacity: 0.3;
  }
  .stats-footer {
    :deep(span) {
      display: inline-block;
      font-size: 12px !important;
      letter-spacing: 0 !important;
    }
  }
}
</style>
